import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { fetchData } from 'services/api/fetchData';
import { formatEmailListDate } from 'services/utils/dateTimeUtils';
import MultiGroupSelectButton from 'components/buttons/MultiGroupSelectButton';
import { renderTicketStatusBadge } from './TicketStatusBadge';
import { Ticket, TicketCategory, TicketInboxView, TicketStatus, TicketViewCounts, TICKET_STATUSES } from './TicketTypes';

// The selected filters of the inbox column per filter group
type InboxFilters = { [groupId: string]: (string | number)[] };

// The unresolved statuses shown by default so every open request is visible at once
const defaultStatuses: TicketStatus[] = ['new', 'open', 'parked', 'awaiting'];

// The default filters, all unresolved requests of all users without a category
const defaultFilters: InboxFilters = { statuses: defaultStatuses, scope: ['all'], categories: [] };

// The statuses a header tab selects in the status filter group
const viewStatuses: Record<TicketInboxView, TicketStatus[]> = {
    inbox: defaultStatuses,
    open: ['open'],
    parked: ['parked'],
    awaiting: ['awaiting'],
    resolved: ['resolved'],
};

// The number of tickets loaded per page
const PAGE_SIZE = 50;

interface TicketInboxProps {
    activeTicketId: number | null;
    refreshKey: number;
    view: TicketInboxView;
    onTicketsLoaded?: (tickets: Ticket[]) => void;
    onCountsLoaded?: (counts: TicketViewCounts) => void;
}

// The ticket list column of the ticket page, shows the tickets as cards below each other
const TicketInbox: React.FC<TicketInboxProps> = ({ activeTicketId, refreshKey, view, onTicketsLoaded, onCountsLoaded }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { handleLogout } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const [filters, setFilters] = useState<InboxFilters>(defaultFilters);
    const [categories, setCategories] = useState<TicketCategory[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    // Keep the logout handler in a ref, its identity changes on every render
    const handleLogoutRef = useRef(handleLogout);
    handleLogoutRef.current = handleLogout;

    // Keep the loaded callback in a ref so a refetch always reports to the current page state
    const onTicketsLoadedRef = useRef(onTicketsLoaded);
    onTicketsLoadedRef.current = onTicketsLoaded;

    // Fetch the categories once for the category filter group
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const data = await fetchData({ apiUrl: 'get_ticket_category_list', params: { deleted: false, is_active: true }, handleLogout: handleLogoutRef.current });
                if (data?.results) setCategories(data.results);
            } catch (error) {
                console.error('Failed to load ticket categories', error);
            }
        };
        loadCategories();
    }, []);

    // Build the query params of the selected filters
    const filterParams = useMemo(() => ({
        status: (filters.statuses ?? []).join(','),
        ...(filters.scope?.includes('mine') ? { assignee: 'me' } : {}),
        ...(filters.categories?.length ? { category: filters.categories.join(',') } : {}),
    }), [filters]);

    // Fetch a page of tickets of the selected filters
    const fetchPage = useCallback(async (pageToLoad: number, replace: boolean) => {
        setLoading(true);
        try {
            const data = await fetchData({
                apiUrl: 'get_ticket_list',
                params: { ...filterParams, deleted: false, page: pageToLoad, page_size: PAGE_SIZE },
                handleLogout: handleLogoutRef.current
            });
            if (data?.results) {
                setTickets(prev => {
                    const updated = replace ? data.results : [...prev, ...data.results];
                    onTicketsLoadedRef.current?.(updated);
                    return updated;
                });
                setTotalCount(data.count ?? data.results.length);
                setPage(pageToLoad);
                if (data.meta_data?.view_counts) onCountsLoaded?.(data.meta_data.view_counts);
            }
        } catch (error) {
            console.error('Failed to load tickets', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [filterParams]);

    // A header tab selects its statuses, the inbox tab resets every filter
    useEffect(() => {
        setFilters(view === 'inbox' ? defaultFilters : current => ({ ...current, statuses: viewStatuses[view] }));
    }, [view]);

    // Load the first page when the filters change or the conversation changed
    useEffect(() => {
        fetchPage(1, true);
    }, [fetchPage, refreshKey]);

    // Load the next page when scrolling to the bottom of the list
    const handleScroll = () => {
        const element = listRef.current;
        if (!element || loading || tickets.length >= totalCount) return;
        if (element.scrollTop + element.clientHeight >= element.scrollHeight - 10) {
            fetchPage(page + 1, false);
        }
    };

    // The filter groups of the filter button, the statuses, the categories and then the scope
    const filterGroups = useMemo(() => [
        {
            id: 'statuses',
            label: 'ticket.filters.status_group_label',
            options: TICKET_STATUSES.map(status => ({ value: status, label: `ticket.status.${status}` }))
        },
        ...(categories.length > 0 ? [{
            id: 'categories',
            label: 'ticket.filters.category_group_label',
            options: categories.map(category => ({ value: category.id, label: category.name }))
        }] : []),
        {
            id: 'scope',
            label: 'ticket.filters.scope_group_label',
            options: [
                { value: 'mine', label: 'ticket.tabs.mine_label' },
                { value: 'all', label: 'ticket.tabs.all_label' },
            ]
        },
    ], [categories]);

    // Keep one selected value in a group, the newly added value replaces the previous one
    const singleSelection = (groupId: string, newFilters: InboxFilters) => {
        const previous = filters[groupId] ?? [];
        const current = newFilters[groupId] ?? [];
        const added = current.filter(value => !previous.includes(value));
        if (added.length > 0) return [added[0]];
        return current.length > 0 ? current : previous;
    };

    // Apply a filter change, the scope group allows one selection
    const handleFilterChange = (newFilters: InboxFilters) => {
        setFilters({
            ...newFilters,
            scope: singleSelection('scope', newFilters),
        });
    };

    return (
        <div className='ticket-inbox'>
            <div className='ticket-inbox-header'>
                <span className='ticket-inbox-title'>{t('ticket.tabs.inbox_label')}</span>
                <MultiGroupSelectButton
                    label='ticket.filters.filter_button_label'
                    size='extra-small'
                    groups={filterGroups}
                    selected={filters}
                    onChange={handleFilterChange}
                />
            </div>
            <div className='ticket-inbox-list' ref={listRef} onScroll={handleScroll}>
                {tickets.map(ticket => (
                    <div key={ticket.id}
                         className={`ticket-inbox-item ${ticket.id === activeTicketId ? 'active' : ''} ${ticket.new_reply ? 'unread' : ''}`}
                         onClick={() => history.push(`/tickets/${ticket.id}`)}>
                        <div className='ticket-inbox-item-row'>
                            <span className='ticket-inbox-item-name'>{ticket.reporter_display_name}</span>
                            <span className='ticket-inbox-item-date'>{ticket.last_message_at ? formatEmailListDate(ticket.last_message_at, userLocale, userTimezone) : ''}</span>
                        </div>
                        <div className='ticket-inbox-item-subject'>{ticket.subject}</div>
                        <div className='ticket-inbox-item-row'>
                            <span className='ticket-inbox-item-meta'>
                                <span className='ticket-inbox-item-number'>#{ticket.number}</span>
                                {ticket.category_name && (
                                    <span className='ticket-inbox-item-category'>{ticket.category_name}</span>
                                )}
                            </span>
                            {renderTicketStatusBadge(ticket.status, ticket.new_reply, true)}
                        </div>
                    </div>
                ))}
                {!loading && tickets.length === 0 && (
                    <p className='ticket-inbox-empty'>{t('ticket.general.inbox_empty_message')}</p>
                )}
                {loading && (
                    <div className='ticket-inbox-loader'><div className='loader' /></div>
                )}
            </div>
        </div>
    );
};

export default TicketInbox;
